import type { z } from "zod";

import {
  avatarAccessResponseSchema,
  avatarCompleteResponseSchema,
  avatarUploadResponseSchema,
} from "@/features/settings/accountSettingsSchemas";
import { browserApiFetch } from "@/shared/api/browserApiClient";

interface CreateAvatarUploadPayload {
  idempotencyKey: string;
  mimeType: string;
  originalName: string;
  sizeBytes: number;
}

export class PrivateAccountSettingsError extends Error {
  readonly data: { error: string };

  constructor(
    readonly status: number,
    errorCode: string,
  ) {
    super(errorCode);
    this.name = "PrivateAccountSettingsError";
    this.data = { error: errorCode };
  }
}

export function createAvatarUploadHeaders(
  signedHeaders: Record<string, string>,
) {
  const headers = new Headers();
  Object.entries(signedHeaders).forEach(([key, value]) => {
    if (key.toLowerCase() !== "content-length") headers.set(key, value);
  });
  return headers;
}

export function createPrivateAvatarUpload(payload: CreateAvatarUploadPayload) {
  return requestPrivateJson(
    "/v1/me/avatar/uploads",
    {
      body: JSON.stringify({
        idempotency_key: payload.idempotencyKey,
        mime_type: payload.mimeType,
        original_name: payload.originalName,
        size_bytes: payload.sizeBytes,
      }),
      headers: { "Content-Type": "application/json" },
      method: "POST",
    },
    avatarUploadResponseSchema,
  );
}

export function completePrivateAvatarUpload(uploadId: string) {
  return requestPrivateJson(
    `/v1/me/avatar/uploads/${encodeURIComponent(uploadId)}/complete`,
    {
      body: JSON.stringify({ parts: [] }),
      headers: { "Content-Type": "application/json" },
      method: "POST",
    },
    avatarCompleteResponseSchema,
  );
}

export function getPrivateAvatarAccess() {
  return requestPrivateJson(
    "/v1/me/avatar/access-url",
    {
      body: JSON.stringify({ required_ttl_seconds: 900 }),
      headers: { "Content-Type": "application/json" },
      method: "POST",
    },
    avatarAccessResponseSchema,
  );
}

async function requestPrivateJson<T>(
  path: string,
  init: RequestInit,
  schema: z.ZodType<T>,
) {
  const response = await browserApiFetch(path, init);
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const errorCode =
      payload &&
      typeof payload === "object" &&
      "error" in payload &&
      typeof payload.error === "string"
        ? payload.error
        : "account_settings_request_failed";
    throw new PrivateAccountSettingsError(response.status, errorCode);
  }
  return schema.parse(payload);
}
